import {
  CollisionPriority,
  CollisionType,
  type CollisionDetector,
  type Modifiers,
} from '@dnd-kit/abstract';
import { RestrictToElement } from '@dnd-kit/dom/modifiers';
import { move } from '@dnd-kit/helpers';
import { DragDropProvider } from '@dnd-kit/react';
import { useSortable } from '@dnd-kit/react/sortable';
import { ReactNode, RefObject, useRef } from 'react';

import { EditorConditionGroup } from '../editor-model';
import {
  applyTreeOrder,
  removeEmptyGroups,
  type TreeOrder,
  type UpdateRule,
} from './condition-draft';

/** The condition tree's drag session, bridged to the rule via {@link TreeOrder}. */

/** Dragged rows stay inside the root rows column, which contains every container. */
export const columnModifiers = (column: RefObject<HTMLDivElement>): Modifiers => [
  RestrictToElement.configure({ element: () => column.current }),
];

// A group appears in the drag session twice: as an item in its parent's list
// (so a leaf can displace it) and as a container (so a leaf can enter it). The
// two need distinct droppable ids — the container keeps the group id, since
// `move` matches it against the item-record keys; the parent-list item gets
// this alias.
const blockId = (groupId: string) => `block:${groupId}`;
const unwrapBlockId = (id: string) => (id.startsWith('block:') ? id.slice('block:'.length) : id);

/** The dnd view of the tree: one id list per container, groups aliased in their parent's list. */
const toDragItems = (root: EditorConditionGroup): Record<string, string[]> => {
  const items: Record<string, string[]> = {};
  const indexTree = (group: EditorConditionGroup): void => {
    items[group.id] = group.children.map((child) =>
      child.kind === 'group' ? blockId(child.id) : child.id,
    );
    for (const child of group.children) {
      if (child.kind === 'group') {
        indexTree(child);
      }
    }
  };
  indexTree(root);
  return items;
};

const toTreeOrder = (items: Record<string, string[]>): TreeOrder =>
  Object.fromEntries(
    Object.entries(items).map(([containerId, ids]) => [containerId, ids.map(unwrapBlockId)]),
  );

// Within this distance of a group block's top/bottom edge, a drag reads as
// root-level displacement rather than entering the group.
const GROUP_EDGE_BAND = 16;

/**
 * Resolves the displace-vs-enter ambiguity on a group block: only its edge
 * bands collide, at a priority beating the rows inside, so the interior is
 * left to the container and its rows (which insert).
 */
const detectGroupCollision: CollisionDetector = ({ droppable, dragOperation }) => {
  const pointer = dragOperation.position.current;
  const { shape } = droppable;
  if (!pointer || !shape || !shape.containsPoint(pointer)) {
    return null;
  }
  const { boundingRectangle } = shape;
  const withinBand =
    pointer.y <= boundingRectangle.top + GROUP_EDGE_BAND ||
    pointer.y >= boundingRectangle.bottom - GROUP_EDGE_BAND;
  if (!withinBand) {
    return null;
  }
  return {
    id: droppable.id,
    value: 1,
    type: CollisionType.PointerIntersection,
    priority: CollisionPriority.Highest,
  };
};

/**
 * The whole tree's drag session. Order commits on drag-over, not drag-end: the
 * optimistic sort shifts the dragged item's index mid-drag, so by drop time it
 * is its own target and a drag-end `move` would compute a no-op. A drop sweeps
 * any group the drag emptied; a canceled drag restores the drag-start tree.
 */
export function TreeReorderProvider({
  root,
  updateRule,
  children,
}: {
  root: EditorConditionGroup;
  updateRule: UpdateRule;
  children: ReactNode;
}) {
  const items = toDragItems(root);
  const snapshot = useRef<TreeOrder | null>(null);
  return (
    <DragDropProvider
      onDragStart={() => {
        snapshot.current = toTreeOrder(items);
      }}
      onDragOver={(event) => {
        const next = toTreeOrder(move(items, event));
        updateRule((rule) => applyTreeOrder(rule, next));
      }}
      onDragEnd={(event) => {
        const restore = snapshot.current;
        snapshot.current = null;
        if (event.canceled) {
          if (restore) {
            updateRule((rule) => applyTreeOrder(rule, restore));
          }
          return;
        }
        updateRule(removeEmptyGroups);
      }}
    >
      {children}
    </DragDropProvider>
  );
}

/**
 * A group's place in the root list: a drop target a leaf can displace (via the
 * edge bands) but never grab.
 */
export function SortableGroupBlock({
  group,
  rootId,
  index,
  children,
}: {
  group: EditorConditionGroup;
  rootId: string;
  index: number;
  children: ReactNode;
}) {
  const { ref, handleRef } = useSortable({
    id: blockId(group.id),
    index,
    group: rootId,
    // A drag-emptied group stops colliding as a root item entirely: its edge
    // bands would otherwise cover most of the shrunken box and displace, making
    // it impossible to drag the leaf back in.
    disabled: { draggable: true, droppable: group.children.length === 0 },
    collisionDetector: detectGroupCollision,
  });
  return (
    <div ref={ref}>
      {/* dnd-kit decorates the activator (handle, else the element) with aria
          attributes incl. aria-disabled — a dead handle keeps them off the
          group's live subtree, which would otherwise read as disabled. */}
      <span ref={handleRef} aria-hidden hidden tabIndex={-1} />
      {children}
    </div>
  );
}
